import { useEffect, useState } from "react";
import { LANGUAGES } from "./Languages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Native-language copy for the auto-detect banner. The first line is shown
// in the visitor's own language so it feels personal, the button is the CTA.
const COPY = {
  de: { msg: "Dieses Buch ist auch auf Deutsch verfügbar.",   cta: "Auf Deutsch hören" },
  fr: { msg: "Ce livre est aussi disponible en français.",     cta: "Écouter en français" },
  es: { msg: "Este libro también está disponible en español.", cta: "Escuchar en español" },
  it: { msg: "Questo libro è disponibile anche in italiano.",  cta: "Ascolta in italiano" },
  el: { msg: "Αυτό το βιβλίο είναι διαθέσιμο και στα Ελληνικά.", cta: "Άκου στα Ελληνικά" },
  tr: { msg: "Bu kitap Türkçe olarak da mevcut.",              cta: "Türkçe dinle" },
  ar: { msg: "هذا الكتاب متوفر أيضًا باللغة العربية.",            cta: "استمع بالعربية" },
  nl: { msg: "Dit boek is ook beschikbaar in het Nederlands.", cta: "Luister in het Nederlands" },
  ru: { msg: "Эта книга также доступна на русском.",           cta: "Слушать на русском" },
  zh: { msg: "本书也提供中文版本。",                            cta: "用中文聆听" },
  hi: { msg: "यह पुस्तक हिंदी में भी उपलब्ध है।",                  cta: "हिंदी में सुनें" },
};

const RTL = new Set(["ar"]);
const DISMISS_KEY = "tb-lang-banner-dismissed";

function detectLang() {
  if (typeof navigator === "undefined") return null;
  // navigator.languages is ordered by user preference, fall back to .language
  const list = navigator.languages?.length ? navigator.languages : [navigator.language || ""];
  for (const raw of list) {
    const code = (raw || "").toLowerCase().split("-")[0];
    if (code && code !== "en" && COPY[code]) return code;
  }
  return null;
}

export default function LanguageRouterBanner() {
  const [target, setTarget] = useState(null);
  const [available, setAvailable] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const code = detectLang();
    if (!code) return;
    // Respect prior dismissal for this specific language
    if (localStorage.getItem(`${DISMISS_KEY}-${code}`) === "1") return;
    setTarget(code);
    // Confirm we actually have at least some content in that language
    fetch(`${API}/book/translations`)
      .then((r) => r.json())
      .then((d) => {
        const t = (d.translations || []).find((x) => x.code === code);
        if (t && (t.chapter_count > 0 || t.audio_count > 0)) setAvailable(true);
      })
      .catch(() => {});
  }, []);

  if (!target || !available) return null;

  const lang = LANGUAGES.find((l) => l.code === target);
  const copy = COPY[target];
  const isRtl = RTL.has(target);

  const dismiss = () => {
    localStorage.setItem(`${DISMISS_KEY}-${target}`, "1");
    setClosing(true);
    setTimeout(() => setTarget(null), 220);
  };

  const openIt = () => {
    // Notify the LanguagePicker to open the reader/player for this language
    window.dispatchEvent(new CustomEvent("tb:open-lang", { detail: { code: target } }));
    // Smooth scroll the picker into view so the player appears in context
    const el = document.getElementById("lang");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Don't auto-dismiss — visitor may want to come back to it
  };

  return (
    <div
      className={"lang-router-banner" + (closing ? " is-closing" : "")}
      dir={isRtl ? "rtl" : "ltr"}
      data-testid="lang-router-banner"
      role="region"
      aria-label="Language suggestion"
    >
      <div className="lang-router-inner">
        <span className="lang-router-flag" aria-hidden="true">{lang?.flag || "🌐"}</span>
        <span className="lang-router-msg">{copy.msg}</span>
        <button
          type="button"
          className="lang-router-cta"
          onClick={openIt}
          data-testid="lang-router-cta"
        >
          {copy.cta} <span aria-hidden="true">→</span>
        </button>
        <button
          type="button"
          className="lang-router-close"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="lang-router-close"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
